import { ContextInbox } from '@/features/inbox/conversations';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, Lock, MessageSquare, Trash2 } from 'lucide-react';
import { useState } from 'react';
import type { CommentDto } from '@cw/shared';
import { Avatar } from '@/design-system/components/avatar';
import { Button } from '@/design-system/components/button';
import { Textarea } from '@/design-system/components/input';
import { plainToDoc, RichView } from '@/design-system/components/rich-view';
import { Segmented } from '@/design-system/components/segmented';
import { Skeleton } from '@/design-system/components/skeleton';
import { relativeTime } from '@/features/activity/activity-text';
import { api } from '@/lib/api';
import { handleFormError } from '@/lib/forms';
import { useCan } from '@/lib/session';
import { taskKeys, useComments } from '@/lib/tasks';
import { cn } from '@/lib/utils';

/** Conversa sobre qualquer item comentável (tarefa, solicitação; arquivos e boards nas próximas fases). */
function LegacyCommentThread({
  entityType,
  entityId,
  onChanged,
  allowClientVisibility = false,
}: {
  entityType: string;
  entityId: string;
  onChanged?: () => void;
  allowClientVisibility?: boolean;
}) {
  const qc = useQueryClient();
  const comments = useComments(entityType, entityId);
  const canInternal = useCan('comments:internal');
  const [text, setText] = useState('');
  const [visibility, setVisibility] = useState<'internal' | 'client'>('internal');
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: taskKeys.comments(entityType, entityId) });
    onChanged?.();
  };
  const create = useMutation({
    mutationFn: () =>
      api.post<CommentDto>(`/comments/${entityType}/${entityId}`, {
        body: plainToDoc(text.trim()),
        visibility: canInternal ? visibility : 'client',
      }),
    onSuccess: () => {
      setText('');
      invalidate();
    },
    onError: (e) => handleFormError(e),
  });
  const update = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'open' | 'resolved' }) =>
      api.patch(`/comments/${id}`, { status }),
    onSuccess: invalidate,
    onError: (e) => handleFormError(e),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/comments/${id}`),
    onSuccess: invalidate,
    onError: (e) => handleFormError(e),
  });

  return (
    <div className="flex flex-col gap-4">
      {comments.isPending ? (
        <Skeleton className="h-16" />
      ) : comments.data?.length ? (
        <ul className="flex flex-col gap-4">
          {comments.data.map((c) => (
            <li key={c.id} className={cn('group flex gap-3', c.status === 'resolved' && 'opacity-60')}>
              <Avatar name={c.authorName} src={c.author?.avatarUrl} size="sm" />
              <div className="min-w-0 flex-1">
                <p className="flex flex-wrap items-baseline gap-x-2 text-[13px]">
                  <span className="font-medium">{c.authorName}</span>
                  <span className="text-[12px] text-fg-subtle">{relativeTime(c.createdAt)}</span>
                  {c.visibility === 'internal' && allowClientVisibility && (
                    <span className="inline-flex items-center gap-1 text-[11.5px] text-fg-subtle">
                      <Lock className="size-3" /> interno
                    </span>
                  )}
                  {c.status === 'resolved' && <span className="text-[11.5px] text-success">resolvido</span>}
                </p>
                <RichView doc={c.body} className="mt-0.5 text-fg" />
                <div className="mt-1 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100 max-md:opacity-100">
                  <button
                    type="button"
                    onClick={() =>
                      update.mutate({ id: c.id, status: c.status === 'open' ? 'resolved' : 'open' })
                    }
                    className="inline-flex items-center gap-1 rounded-[6px] px-1.5 py-0.5 text-[12px] text-fg-muted hover:bg-hover hover:text-fg"
                  >
                    <Check className="size-3" /> {c.status === 'open' ? 'Resolver' : 'Reabrir'}
                  </button>
                  {c.mine && (
                    <button
                      type="button"
                      onClick={() => remove.mutate(c.id)}
                      className="inline-flex items-center gap-1 rounded-[6px] px-1.5 py-0.5 text-[12px] text-fg-muted hover:bg-hover hover:text-danger"
                    >
                      <Trash2 className="size-3" /> Excluir
                    </button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="flex items-center gap-2 text-[13px] text-fg-subtle">
          <MessageSquare className="size-4" /> Nenhum comentário ainda.
        </p>
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (text.trim()) create.mutate();
        }}
        className="flex flex-col gap-2"
      >
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && text.trim()) create.mutate();
          }}
          placeholder="Escreva um comentário"
          rows={2}
          className="min-h-16"
          aria-label="Novo comentário"
        />
        <div className="flex items-center justify-between gap-2">
          {canInternal && allowClientVisibility ? (
            <Segmented
              label="Quem vê"
              value={visibility}
              onChange={setVisibility}
              options={[
                { value: 'internal', label: 'Só a equipe' },
                { value: 'client', label: 'Equipe e cliente' },
              ]}
            />
          ) : (
            <span className="text-[12px] text-fg-subtle">⌘ + Enter envia</span>
          )}
          <Button
            type="submit"
            size="sm"
            variant="primary"
            disabled={!text.trim()}
            loading={create.isPending}
          >
            Comentar
          </Button>
        </div>
      </form>
    </div>
  );
}

export function CommentThread(props: Parameters<typeof LegacyCommentThread>[0]) {
  return props.entityType === 'task' || props.entityType === 'request' ? (
    <ContextInbox type={props.entityType} id={props.entityId} />
  ) : (
    <LegacyCommentThread {...props} />
  );
}
